import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isIOS, isStandalone } from "@/lib/install";
import { isInAppBrowser } from "@/lib/googleAuth";

const GUIDES = {
  iphone: {
    label: "iPhone / iPad",
    tips: [
      ["Use Safari", "Google sign-in works best in Safari. If you opened the link from another app, tap the share or ⋯ button and choose Open in Safari."],
      ["Home-screen app", "Tap Continue with Google — a sign-in window opens. Finish signing in, then tap Done (top-left) to return. You'll be signed in automatically within a few seconds."],
      ["Seeing \"Invalid state parameter\"?", "The sign-in started in one window and finished in another. Open the app's address directly in Safari and try again, or use Email me a sign-in link."],
      ["Stuck on a white or loading page?", "Close that window, then tap Open Google again on the sign-in screen. Or use Email me a sign-in link instead."],
      ["Private Browsing", "Turn off Private Browsing, or allow cookies in Settings → Safari → turn off Block All Cookies."],
    ],
  },
  android: {
    label: "Android",
    tips: [
      ["Use Chrome", "Open the app in Chrome. Other browsers built into apps (Messenger, Instagram, Facebook) block Google sign-in."],
      ["Home-screen app", "Tap Continue with Google, choose your account, and you'll be brought back automatically. If it doesn't return, close the Google tab and you'll be signed in within a few seconds."],
      ["Seeing \"Invalid state parameter\"?", "Google sign-in started in one window and finished in another (common with home-screen apps or links opened from other apps). Open the app's address directly in Chrome and try again — or tap Email me a sign-in link, which always works."],
      ["Several Google accounts?", "Pick the account you want on the Google screen. If the wrong one is chosen, sign out of the app in Settings and try again."],
    ],
  },
  inapp: {
    label: "Messenger / Instagram / Facebook",
    tips: [
      ["Why it doesn't work", "Google doesn't allow sign-in inside the browsers built into social and messaging apps, so the page can hang or show an error."],
      ["Fix", "Tap the ⋯ or share menu in the corner and choose Open in browser (or Open in Safari / Chrome). Then sign in there."],
      ["Or", "Use Email me a sign-in link, or your email and password — both work anywhere."],
    ],
  },
  desktop: {
    label: "Computer",
    tips: [
      ["Pop-ups and cookies", "Allow cookies for this site. If you use strict tracking protection (Firefox, Brave, Safari), try another browser or add an exception."],
      ["Wrong Google account", "On the Google screen choose Use another account to pick the right one."],
      ["Still loading?", "Refresh this page, then try again. Or use an email sign-in link."],
    ],
  },
  email: {
    label: "Email links",
    tips: [
      ["Didn't get it?", "Check your spam or promotions folder, and make sure the email address is spelled correctly. You can request up to 3 links every 15 minutes."],
      ["Link expired or already used?", "Each link works once and lasts 15 minutes. Request a new one from the sign-in screen."],
      ["Open on the same device", "Open the email on the phone or computer where you want to use the app. On iPhone home-screen apps, the link opens in Safari — you can keep using the app there, or sign in again in the home-screen app."],
    ],
  },
};

const detect = () => (isInAppBrowser() ? "inapp" : isIOS() ? "iphone" : /android/i.test(navigator.userAgent) ? "android" : "desktop");

export function SignInHelp() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(detect);
  const g = GUIDES[tab];
  return (
    <>
      <button onClick={() => { setTab(detect()); setOpen(true); }} data-testid="signin-help-link"
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-sm text-t-muted hover:text-t-brand transition-colors">
        <HelpCircle size={15} /> Having trouble signing in?
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl bg-t-surface border-t-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="signin-help-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-t-on">Sign-in help</DialogTitle>
            <DialogDescription className="text-t-muted">
              We've picked tips for your device{isStandalone() ? " (home-screen app)" : ""}. Choose another if it's not right.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {Object.entries(GUIDES).map(([k, v]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`signin-help-tab-${k}`}
                className={`px-3.5 h-8 rounded-full text-[13px] font-semibold border transition-colors ${tab === k ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface2 text-t-on2 border-t-border"}`}>{v.label}</button>
            ))}
          </div>
          <div className="flex flex-col gap-3 mt-2" data-testid={`signin-help-content-${tab}`}>
            {g.tips.map(([title, body]) => (
              <div key={title} className="tf-card p-4">
                <p className="font-semibold text-t-on">{title}</p>
                <p className="text-sm text-t-on2 mt-1 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
